/**
 * Traduz as props antigas do `CertificatePreview` para o snapshot do pipeline.
 *
 * POR QUE UM ADAPTADOR, E NÃO REESCREVER AS OITO TELAS
 *
 * O `CertificatePreview` é chamado em oito lugares, cada um montando as props
 * do seu jeito. Trocar tudo de uma vez transforma "o preview passa a usar o
 * pipeline" num diff que ninguém revisa direito — e é um trabalho cuja graça é
 * poder ser conferido.
 *
 * Com o adaptador, muda UM arquivo. As oito telas continuam passando o que
 * sempre passaram e recebem o desenho do pipeline. Quando cada uma migrar para
 * o snapshot direto, este arquivo encolhe até sumir.
 *
 * A LIMITAÇÃO QUE PRECISA SER DITA
 *
 * As props trazem a data JÁ FORMATADA (`"15/10/2026"`), porque o preview antigo
 * só imprimia o que recebia. O pipeline formata sozinho — é uma das correções:
 * hoje existem três formatos diferentes para o mesmo certificado.
 *
 * Então a data precisa voltar a ser ISO aqui. Quem tiver o ISO deve passá-lo
 * em `eventStartsAt`/`eventEndsAt`, e o caminho de reconstrução nem roda. Sem
 * ele, a reconstrução assume o fuso do evento — correto para os fusos
 * brasileiros, que é o universo da plataforma hoje, e registrado como dívida.
 */

import type {
  CertificateFields,
  CertificateFontSizes,
  CertificateSampleData,
  CertificateSigner,
  CertificateTextConfig,
  CertificateTextPositions,
} from "./CertificatePreview";
import type { CertificateElement } from "./CertificateElementTypes";
import type { AssetManifest, AssetRef, CertificateSnapshot } from "@tickethall/core/certificate";

export interface EntradaDoPreview {
  backgroundUrl?: string;
  fields: CertificateFields;
  textConfig: CertificateTextConfig;
  signers: CertificateSigner[];
  workloadHours?: number;
  sampleData: CertificateSampleData;
  textColor?: string;
  textPositions?: CertificateTextPositions;
  fontSizes?: CertificateFontSizes;
  elements?: CertificateElement[];
  /** ISO 8601. Preferir sempre a estes do que deixar reconstruir da string. */
  eventStartsAt?: string | null;
  eventEndsAt?: string | null;
  /** Fuso do evento. O certificado congela o do EVENTO, nunca o do leitor. */
  timeZone?: string;
  organizerName?: string | null;
}

/**
 * Minutos de defasagem por fuso, iguais aos do pipeline.
 *
 * Repetidos aqui de propósito: o pipeline não pode importar nada do app, e o
 * `Intl` é proibido lá dentro (o ICU do Deno Deploy muda sem aviso). Esta
 * tabela existe só para o caminho de reconstrução, que some quando as telas
 * passarem o ISO.
 */
const OFFSET_MIN: Record<string, number> = {
  "America/Sao_Paulo": -180,
  "America/Bahia": -180,
  "America/Fortaleza": -180,
  "America/Recife": -180,
  "America/Belem": -180,
  "America/Manaus": -240,
  "America/Cuiaba": -240,
  "America/Porto_Velho": -240,
  "America/Boa_Vista": -240,
  "America/Campo_Grande": -240,
  "America/Rio_Branco": -300,
  "America/Eirunepe": -300,
  "America/Noronha": -120,
};

/**
 * `"15/10/2026"` + `"09:00"` de volta para ISO.
 *
 * Devolve `null` quando não reconhece — melhor o certificado sair sem data do
 * que com uma data inventada a partir de uma string que não era o que eu
 * supus.
 */
function paraIso(data: string | undefined, hora: string | undefined, fuso: string): string | null {
  const d = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((data ?? "").trim());
  if (!d) return null;

  const h = /^(\d{1,2}):(\d{2})$/.exec((hora ?? "").trim());
  const [, dia, mes, ano] = d;
  const hh = h ? Number(h[1]) : 12;
  const mm = h ? Number(h[2]) : 0;

  const offset = OFFSET_MIN[fuso] ?? -180;
  const utc = Date.UTC(Number(ano), Number(mes) - 1, Number(dia), hh, mm) - offset * 60_000;
  return new Date(utc).toISOString();
}

/** `123.456.789-00` -> `***.456.789-**`. Nunca devolve o documento inteiro. */
function mascarar(cpf: string | undefined): string | null {
  const digitos = (cpf ?? "").replace(/\D/g, "");
  if (digitos.length < 11) return null;
  return `***.${digitos.slice(3, 6)}.${digitos.slice(6, 9)}-**`;
}

/**
 * Data de emissão FIXA no preview.
 *
 * `new Date()` aqui faria o preview mudar de conteúdo a cada renderização, e
 * qualquer comparação com o PDF viraria loteria. O preview mostra o layout, não
 * um certificado emitido — a data de emissão real entra no snapshot gravado.
 */
const EMISSAO_DO_PREVIEW = "2026-01-01T12:00:00.000Z";

/**
 * Manifesto de imagens a partir das URLs que as telas passam.
 *
 * O FUNDO PRECISA VIR POR AQUI, não como um `<img>` atrás do desenho.
 *
 * Quando o manifesto não tem fundo, o `resolve` desenha um retângulo OPACO da
 * cor do papel cobrindo a página inteira — é o comportamento certo dele: sem
 * imagem, o certificado tem um fundo sólido. Uma imagem colocada atrás do SVG
 * ficaria escondida embaixo desse retângulo, e o fundo simplesmente não
 * apareceria.
 *
 * Foi assim que a primeira versão deste componente ficou, e passou por todos os
 * testes: nenhum deles usava fundo de verdade.
 *
 * O `id` é a própria URL enquanto os assets não são endereçados por conteúdo
 * (Fase C). Não é ideal — trocar o arquivo na URL muda o desenho por baixo —
 * mas é o que a tela tem hoje, e o preview não grava nada.
 */
export function manifestoDasProps(
  p: EntradaDoPreview,
  /**
   * Dimensões reais por URL, quando já medidas.
   *
   * O `resolve` USA `w`/`h` para escalar a assinatura preservando a proporção.
   * Enquanto elas não chegam, ele cai numa proporção de reserva — a assinatura
   * aparece, mas com a forma errada, e o PDF (que tem as medidas de verdade)
   * mostraria outra.
   *
   * A versão anterior deste arquivo afirmava que `w`/`h` não eram usados. Era
   * falso, e o efeito era pior que forma errada: `0` fazia a conta produzir
   * `NaN` e a assinatura sumia inteira, sem erro nenhum.
   */
  dimensoes?: ReadonlyMap<string, { w: number; h: number }>,
): {
  manifest: AssetManifest;
  urlDoAsset: (id: string) => string;
} {
  const assets: AssetRef[] = [];
  const urls = new Map<string, string>();

  const acrescentar = (url: string, role: "background" | "signature") => {
    const id = `url:${url}`;
    if (urls.has(id)) return;
    urls.set(id, url);
    const medida = dimensoes?.get(url);
    assets.push({
      id,
      role,
      // O `mime` não muda o desenho no SVG — o navegador decide pelo conteúdo.
      mime: /\.png(\?|$)/i.test(url) ? "image/png" : "image/jpeg",
      w: medida?.w ?? 0,
      h: medida?.h ?? 0,
      src: url,
    });
  };

  if (p.backgroundUrl) acrescentar(p.backgroundUrl, "background");
  for (const s of p.signers ?? []) {
    if (s.signatureUrl) acrescentar(s.signatureUrl, "signature");
  }

  return {
    manifest: { assets },
    urlDoAsset: (id) => urls.get(id) ?? "",
  };
}

export function snapshotDasProps(p: EntradaDoPreview): CertificateSnapshot {
  const fuso = p.timeZone || "America/Sao_Paulo";

  const inicio = p.eventStartsAt ?? paraIso(p.sampleData.eventDate, p.sampleData.eventTime, fuso);
  const fim = p.eventEndsAt ?? inicio;

  return {
    v: 1,
    pipeline: "2.0.0",
    issuedAt: EMISSAO_DO_PREVIEW,
    timeZone: fuso,
    // Exatamente a forma que o `normalizeConfig` já sabe ler — é o mesmo
    // `certificate_config` que está no banco.
    configRaw: {
      backgroundUrl: p.backgroundUrl,
      textColor: p.textColor,
      fields: p.fields,
      textConfig: p.textConfig,
      signers: p.signers,
      workloadHours: p.workloadHours,
      textPositions: p.textPositions,
      fontSizes: p.fontSizes,
      elements: p.elements,
    },
    data: {
      certificateCode: p.sampleData.certificateCode,
      verificationUrl:
        `https://tickethall.com.br/verificar-certificado?code=${encodeURIComponent(p.sampleData.certificateCode)}`,
      attendeeName: p.sampleData.participantName,
      attendeeDocumentMasked: mascarar(p.sampleData.participantCPF),
      eventTitle: p.sampleData.eventName,
      eventStartsAt: inicio,
      eventEndsAt: fim,
      eventDateMode: p.fields?.eventDateMode ?? "start",
      venueName: p.sampleData.eventLocation || null,
      workloadHours: p.workloadHours ?? null,
      organizerName: p.organizerName ?? null,
      checkedInAt: null,
    },
    assets: [],
    fonts: ["inter-400@1", "inter-600@1", "sora-700@1"],
  };
}
